import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarCheck, MessageSquare, TrendingUp, Zap, Moon, Coins } from "lucide-react";

const TILES = [
  { key: "reservas", label: "Reservas / citas cogidas", icon: CalendarCheck },
  { key: "mensajes_atendidos", label: "Mensajes atendidos", icon: MessageSquare },
  { key: "prospectos_contactados", label: "Prospectos contactados", icon: TrendingUp },
  { key: "acciones_ejecutadas", label: "Acciones ejecutadas", icon: Zap },
  { key: "fuera_horario", label: "Atendido fuera de horario", icon: Moon },
];

export default function Resultados() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [valor, setValor] = useState("");
  const [savingV, setSavingV] = useState(false);

  const load = (d) =>
    api.get("/results/overview?days=" + d).then((r) => { setData(r.data); setValor(String(r.data.valor_medio)); }).catch(() => {});
  useEffect(() => { load(days); }, [days]);

  const saveValor = async () => {
    const v = parseFloat(valor);
    if (isNaN(v) || v < 0) return toast.error("Pon un número válido");
    setSavingV(true);
    try { await api.put("/assistant/config", { valor_medio: v }); toast.success("Valor guardado"); load(days); }
    catch (e) { toast.error("Error al guardar"); }
    finally { setSavingV(false); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="resultados-page">
      <div className="border-b border-[var(--border)] pb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono mb-2">RESULTADOS</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Lo que tu agente hizo por ti</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">El valor real que genera tu equipo de IA, en números.</p>
        </div>
        <div className="flex border border-[var(--border)]">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-3 py-1.5 text-xs font-mono ${days === d ? "bg-[var(--accent)] text-[var(--bg)]" : "text-[var(--text-dim)] hover:text-white"}`}
                    data-testid={`res-days-${d}`}>
              {d} días
            </button>
          ))}
        </div>
      </div>

      {!data && <div className="text-sm text-[var(--text-dim)] font-mono">Cargando…</div>}

      {data && (
        <>
          {/* € destacado */}
          <div className="border border-[var(--accent)] bg-[var(--accent)]/[0.06] p-6 flex items-center gap-5">
            <div className="w-14 h-14 border border-[var(--accent)] flex items-center justify-center shrink-0">
              <Coins className="w-7 h-7 text-[var(--accent)]" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-black text-5xl tracking-tight text-[var(--accent)]">
                ≈ {data.ingresos_estimados.toLocaleString("es-ES")} €
              </div>
              <div className="text-sm text-[var(--text-dim)] mt-1">
                Facturación estimada que tu agente ayudó a generar en los últimos {data.days} días.
              </div>
            </div>
          </div>

          {/* Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {TILES.map((t) => (
              <div key={t.key} className="border border-[var(--border)] bg-[var(--surface)] p-5">
                <t.icon className="w-5 h-5 text-[var(--accent)] mb-3" strokeWidth={2} />
                <div className="font-display font-black text-3xl tracking-tight tabular-nums">{data[t.key]}</div>
                <div className="label-mono mt-1 leading-tight">{t.label}</div>
              </div>
            ))}
          </div>

          {/* Valor medio editable */}
          <div className="border border-[var(--border)] bg-[var(--surface)] p-5 space-y-2">
            <div className="label-mono">AJUSTA TU VALOR MEDIO</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-[var(--text-dim)]">Valor medio por cliente/reserva:</span>
              <Input type="number" value={valor} onChange={(e) => setValor(e.target.value)}
                     className="rounded-none bg-[var(--bg)] border-[var(--border)] w-28 font-mono" data-testid="res-valor" />
              <span className="text-sm text-[var(--text-dim)]">€</span>
              <Button onClick={saveValor} disabled={savingV}
                      className="rounded-none h-9 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="res-save-valor">
                {savingV ? "Guardando…" : "Guardar"}
              </Button>
            </div>
            <p className="text-xs text-[var(--text-dim)]">
              Con esto estimamos los € generados (reservas × valor medio). Ponlo acorde a tu ticket medio o al valor de un cliente nuevo.
            </p>
          </div>

          <p className="text-xs text-[var(--text-dim)] font-mono">
            Cifras reales de tu actividad de los últimos {data.days} días. Los € son una estimación orientativa basada en tu valor medio.
          </p>
        </>
      )}
    </div>
  );
}
